import { Injectable } from '@angular/core';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  shareReplay,
} from 'rxjs';
import { CountryAddressService } from './country-address.service';
import { DataService } from './data.service';

export interface TenantLabels {
  subNationalLabel: string;
  localLevelLabel: string;
}

/**
 * Centralizes the tenant's country-specific address terminology so the app
 * stops hardcoding "State"/"LGA" everywhere: Nigeria's tenantCountry returns
 * subNationalLabel: "State" / localLevelLabel: "LGA", but e.g. Ghana returns
 * "Region" / "District". Bind [subNationalLabel]/[localLevelLabel] directly
 * in templates instead of a literal "State"/"LGA" mat-label.
 *
 * Defaults match the pre-existing hardcoded text so nothing regresses before
 * the tenant-and-country call (already cached by DataService) resolves.
 *
 * The tenant labels are the app-wide DEFAULT. A form that lets the user pick
 * a country per record (client onboarding, for example) should not use them
 * for its own address fields — a Ghana tenant onboarding a Nigerian client
 * must show "State"/"LGA" for that client even though the tenant default is
 * "Region"/"District". Those forms call `labelsForCountry(countryCode)` and
 * bind the result instead.
 *
 * Template bindings can read the plain subNationalLabel/localLevelLabel
 * properties directly — Angular's own change detection re-reads them every
 * cycle, so they pick up the update once the API resolves with no extra
 * plumbing. A plain TS structure built once (e.g. a table's column-definition
 * array, built in a constructor) does NOT get re-checked that way — it needs
 * to explicitly subscribe to labels$ and update itself when the real value
 * arrives, since the constructor almost always runs before this service's own
 * subscription below has resolved.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantLabelsService {
  public subNationalLabel = 'State';
  public localLevelLabel = 'LGA';

  public readonly labels$: Observable<TenantLabels>;

  constructor(
    private dataService: DataService,
    private countryAddress: CountryAddressService,
  ) {
    this.labels$ = this.dataService.getOrganization().pipe(
      map((res) => {
        const tenantCountry = res?.data?.tenantCountry;
        return {
          subNationalLabel:
            tenantCountry?.subNationalLabel || this.subNationalLabel,
          localLevelLabel:
            tenantCountry?.localLevelLabel || this.localLevelLabel,
        };
      }),
      shareReplay(1),
    );

    this.labels$.subscribe((labels) => {
      this.subNationalLabel = labels.subNationalLabel;
      this.localLevelLabel = labels.localLevelLabel;
    });
  }

  /**
   * Address labels for a SPECIFIC country, e.g. the one picked in a client's
   * address block. Each country returned by the country-data API carries its
   * own subNationalLabel/localLevelLabel, so selecting Nigeria yields
   * "State"/"LGA" and Ghana yields its own terms, independent of the tenant.
   *
   * Falls back to the tenant labels when the code is empty, unknown, blank on
   * that country's record, or the lookup fails — so a dropdown never ends up
   * with an empty mat-label.
   */
  public labelsForCountry(countryCode: string): Observable<TenantLabels> {
    if (!countryCode) return this.labels$;
    return combineLatest([
      this.countryAddress.getCountryData().pipe(catchError(() => of([]))),
      this.labels$,
    ]).pipe(
      map(([countries, fallback]) => {
        const match = countries.find(
          (c) => c.countryCode === countryCode || c.uuid === countryCode,
        );
        return {
          subNationalLabel:
            match?.subNationalLabel || fallback.subNationalLabel,
          localLevelLabel: match?.localLevelLabel || fallback.localLevelLabel,
        };
      }),
    );
  }
}
